// In-browser port of the forecast's per-segment pricing, so a slider drag is
// priced instantly with the server's own math. The what-if POST stays the
// authoritative reconciliation.
//
// Mirrors (keep in step with):
//   apps/dashboard/services/forecast.py        cpl_at_spend, _scenario_rates,
//                                               _project, segment_outcomes_at
//   apps/app1/services/projection_calculator.py project_funnel
//   apps/dashboard/services/forecast_api.py     _serialize_outputs, _clamp,
//                                               recompute_whatif (roll-up)

export const SCENARIOS = ['low', 'expected', 'high'];
const DEFAULT_CPL_FLOOR = 0.5; // forecast.CPL_CURVE_FLOOR

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const roundTo = (v, n = 2) => {
  if (v == null || Number.isNaN(Number(v))) return null;
  const f = 10 ** n;
  return Math.round(Number(v) * f) / f;
};

/** CPL at a MONTHLY spend level on the elasticity curve, floored. */
export function cplAtSpend(monthlySpend, { cplAtBase, k, spendBaseDaily, daysInMonth, floor = DEFAULT_CPL_FLOOR }) {
  const base = toNum(cplAtBase);
  if (base <= 0) return 0;
  const kk = toNum(k);
  if (kk <= 0 || !spendBaseDaily || !monthlySpend) return base;
  const daily = toNum(monthlySpend) / Math.max(1, daysInMonth || 1);
  if (daily <= 0) return base;
  const cpl = base * (1 + kk * Math.log(daily / toNum(spendBaseDaily)));
  return Math.max(cpl, base * floor);
}

/** low = pricier leads + weaker close/job size; high = the reverse. */
export function scenarioRates(baseRates, scenario, { cplBand, rateBand, cplCov }) {
  const rates = { ...baseRates };
  if (scenario === 'expected') return rates;
  const band = toNum(cplBand) + toNum(cplCov);
  const rband = toNum(rateBand);
  const sign = scenario === 'low' ? 1 : -1;
  if ('cpl' in rates) rates.cpl = toNum(rates.cpl) * (1 + sign * band);
  ['close_rate', 'avg_job_size'].forEach((key) => {
    if (key in rates) rates[key] = toNum(rates[key]) * (1 - sign * rband);
  });
  return rates;
}

/** project_funnel: spend + rates -> lead/appointment/sale/revenue chain. */
export function projectFunnel(rates, spend) {
  const s = toNum(spend);
  const cpl = toNum(rates.cpl);
  const leads = cpl ? s / cpl : 0;
  const setAppointments = leads * toNum(rates.set_rate);
  const issues = setAppointments * toNum(rates.issue_rate);
  const demos = issues * toNum(rates.demo_rate);
  const netSold = demos * toNum(rates.close_rate);
  const netRevenue = netSold * toNum(rates.avg_job_size);
  return {
    spend: s,
    leads,
    set_appointments: setAppointments,
    issues,
    demos,
    net_sold: netSold,
    net_revenue: netRevenue,
    cost_of_marketing: netRevenue ? s / netRevenue : null,
  };
}

/** _serialize_outputs: the keys + rounding the API ships. */
export function serializeOutputs(out) {
  return {
    spend: roundTo(out.spend, 2),
    leads: roundTo(out.leads, 1),
    appointments: roundTo(out.set_appointments, 1),
    booked: roundTo(out.net_sold, 1),
    net_revenue: roundTo(out.net_revenue, 0),
    com: roundTo(out.cost_of_marketing, 4),
  };
}

/** The [floor, ceiling] a budget is clamped to; the ceiling never sits below
 * the current budget, so the status quo is always modelable. */
export function budgetBounds(segment) {
  const base = toNum(segment?.base_budget);
  const lo = segment?.spend_floor == null ? null : toNum(segment.spend_floor);
  const hiRaw = Math.max(toNum(segment?.spend_ceiling), base);
  return { lo, hi: hiRaw || null };
}

export function clampBudget(segment, budget) {
  const { lo, hi } = budgetBounds(segment);
  let v = toNum(budget);
  const requested = v;
  if (lo != null && v < lo) v = lo;
  if (hi != null && v > hi) v = hi;
  return { budget: v, clamped: v !== requested };
}

/** Raw (unrounded) low/expected/high funnel outcomes at an exact budget. */
export function rawOutcomesAt(segment, budget, daysInMonth) {
  const model = segment?.model || {};
  const curve = segment?.curve || {};
  const baseRates = { ...(model.base_rates || {}) };
  baseRates.cpl = cplAtSpend(budget, {
    cplAtBase: curve.cpl_at_base || baseRates.cpl || 0,
    k: curve.k || 0,
    spendBaseDaily: curve.spend_base_daily,
    daysInMonth,
    floor: model.cpl_floor ?? DEFAULT_CPL_FLOOR,
  });
  const band = { cplBand: model.cpl_band, rateBand: model.rate_band, cplCov: model.cpl_cov };
  const out = {};
  SCENARIOS.forEach((s) => {
    out[s] = projectFunnel(scenarioRates(baseRates, s, band), budget);
  });
  return out;
}

/**
 * Price one serialized segment at a monthly budget.
 * Returns {low, expected, high, applied_budget, clamped}; each scenario has
 * spend, leads, appointments, booked, net_revenue, com (API keys/rounding).
 */
export function segmentOutcomesAt(segment, budget, daysInMonth) {
  const { budget: applied, clamped } = clampBudget(segment, budget);
  const raw = rawOutcomesAt(segment, applied, daysInMonth);
  const out = { applied_budget: roundTo(applied, 2), clamped };
  SCENARIOS.forEach((s) => {
    out[s] = serializeOutputs(raw[s]);
  });
  return out;
}

/**
 * Roll segment outputs ({low, expected, high} each) up to client totals.
 * CoM divides only the spend that has revenue behind it (``com_spend``), the
 * same rule the GET roll-up uses.
 */
export function rollUp(segmentOutputs) {
  const scenarios = {};
  SCENARIOS.forEach((s) => {
    let spend = 0;
    let leads = 0;
    let appointments = 0;
    let booked = 0;
    let revenue = 0;
    let comSpend = 0;
    (segmentOutputs || []).forEach((o) => {
      const r = o?.[s];
      if (!r) return;
      spend += toNum(r.spend);
      leads += toNum(r.leads);
      appointments += toNum(r.appointments);
      booked += toNum(r.booked);
      if (toNum(r.net_revenue)) {
        revenue += toNum(r.net_revenue);
        comSpend += toNum(r.spend);
      }
    });
    scenarios[s] = {
      spend: roundTo(spend, 2),
      leads: roundTo(leads, 1),
      cpl: leads ? roundTo(spend / leads, 2) : null,
      com_spend: roundTo(comSpend, 2),
      appointments: roundTo(appointments, 1),
      booked: roundTo(booked, 1),
      net_revenue: roundTo(revenue, 0),
      com: revenue ? roundTo(comSpend / revenue, 4) : null,
    };
  });
  return scenarios;
}

/**
 * The what-if, in the browser: price every segment at ``budgets`` (budget_id
 * -> monthly $). A segment left at its base budget returns its baseline
 * outputs verbatim, so dragging one slider never moves another row.
 * Same shape as the what-if POST response ({scenarios, segments}).
 */
export function localWhatIf(segments, budgets, daysInMonth) {
  const rows = (segments || []).map((seg) => {
    const requested = budgets?.[seg.budget_id] ?? seg.base_budget;
    const { budget, clamped } = clampBudget(seg, requested);
    const untouched = Math.abs(budget - toNum(seg.base_budget)) < 0.005;
    const priced = untouched ? null : segmentOutcomesAt(seg, budget, daysInMonth);
    const outputs = {};
    SCENARIOS.forEach((s) => {
      outputs[s] = untouched ? seg.outputs?.[s] : priced[s];
    });
    return {
      budget_id: seg.budget_id,
      scope_label: seg.scope_label,
      scope_mode: seg.scope_mode,
      base_budget: seg.base_budget,
      applied_budget: roundTo(budget, 2),
      clamped,
      outputs,
    };
  });
  return { scenarios: rollUp(rows.map((r) => r.outputs)), segments: rows };
}

export function daysInPeriod(period) {
  if (!period) return 30;
  const [y, m] = String(period).split('-').map(Number);
  if (!y || !m) return 30;
  return new Date(y, m, 0).getDate();
}
